/**
 * useFramework — 프레임워크 전역 facade 훅.
 *
 * 【역할】
 * - 여러 개별 훅(useTransaction/useMessage/useLabel/...) 을 하나의 객체로 묶어 페이지 코드를 단순화.
 * - vue-spring-fw 의 `$fw.xxx` 접근 패턴을 React 로 포팅 (Vue globalProperties 유사).
 *
 * 【주요 기능】
 * - $createDataSet: useDataSet 별명 (페이지 최상단에서 호출)
 * - $transaction(config): RPC 디스패처 래퍼
 * - $msg: useMessage 결과 (success/byId/confirm 등)
 * - $t: label 함수
 * - $locale / $perm / $popup / $codes: 각 훅 반환 그대로
 * - loading / setLoading: 전역 로딩 상태
 *
 * 【facade 패턴 설명】
 * - 페이지 당 상단에 `const $fw = useFramework()` 1회 호출로 모든 프레임워크 기능에 접근.
 * - 개별 훅 직접 사용도 가능 (facade 가 강제는 아님) — 번들 크기 우려 시 import 분리 권장.
 * - `$createDataSet` 은 훅 자체 참조이므로 컴포넌트 최상단에서만 호출 가능 (closure 내부 ✗).
 *
 * 【사용 예시】
 * ```tsx
 * const { $createDataSet, $transaction, $msg } = useFramework()
 * const dsUser = $createDataSet<UserRow>()
 * await $transaction({ id: 'saveUsers', service: 'user', inds: { ds_user: dsUser } })
 * await $msg.byId('MSG_SAVE_SUCCESS')
 * ```
 *
 * 【왜 이 패턴?】
 * - vue-spring-fw (Vue) 에서 Composition API 를 거의 동일 형태로 이식하여 러닝 커브 최소화.
 */
import { useCallback } from 'react'
import { useFrameworkContext } from './FrameworkProvider'
import { useDataSet, type UseDataSetResult } from './useDataSet'
import { useTransaction, type TransactionConfig } from './useTransaction'
import { useMessage } from './useMessage'
import { useLabel } from './useLabel'
import { useLocale } from './useLocale'
import { usePermission } from './usePermission'
import { usePopup } from './usePopup'
import { useCodes } from './useCodes'

/**
 * 프레임워크 전역 facade — 페이지 최상단에서 1회 호출.
 * @returns $createDataSet/$transaction/$msg/$t/$locale/$perm/$popup/$codes + loading 상태.
 */
export function useFramework() {
  const ctx = useFrameworkContext()
  const { transaction } = useTransaction()
  const msg = useMessage()
  const labels = useLabel()
  const locale = useLocale()
  const perm = usePermission()
  const popup = usePopup()
  const codes = useCodes()

  /**
   * $createDataSet — 훅 호출이 제한된 곳(렌더링 외부)에서는 사용 불가.
   * 컴포넌트 최상단에서만 호출할 것.
   */
  const $createDataSet = useDataSet

  const $transaction = useCallback(
    (config: TransactionConfig) => transaction(config),
    [transaction]
  )

  return {
    $createDataSet,
    $transaction,
    $msg: msg,
    $t: labels.t,
    $locale: locale,
    $perm: perm,
    $popup: popup,
    $codes: codes,
    loading: ctx.loading,
    setLoading: ctx.setLoading
  }
}

export type { UseDataSetResult }
